// Cookie management utilities for game sessions

export interface GameSessionCookie {
  gameId: string;
  primers: string[];
  createdAt: number;
  lastPlayed: number;
}

export class CookieManager {
  private static readonly COOKIE_PREFIX = 'unchained_game_';
  private static readonly TTL_HOURS = 24;

  // Store a game session in a cookie
  static saveGameSession(gameId: string, primers: string[]): void {
    
    const sessionData: GameSessionCookie = {
      gameId,
      primers,
      createdAt: Date.now(),
      lastPlayed: Date.now(),
    };

    const cookieName = `${this.COOKIE_PREFIX}${gameId}`;
    const cookieValue = encodeURIComponent(JSON.stringify(sessionData));
    const expires = new Date();
    expires.setTime(expires.getTime() + (this.TTL_HOURS * 60 * 60 * 1000));
    
    document.cookie = `${cookieName}=${cookieValue}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  }

  // Update last played time for a game session
  static updateLastPlayed(gameId: string): void {
    const session = this.getGameSession(gameId);
    if (session) {
      session.lastPlayed = Date.now();
      this.saveGameSession(session.gameId, session.primers);
    }
  }

  // Get all active game sessions from cookies
  static getActiveGameSessions(): GameSessionCookie[] {
    const cookies = document.cookie.split(';');
    const sessions: GameSessionCookie[] = [];
    const now = Date.now();
    const ttlMs = this.TTL_HOURS * 60 * 60 * 1000;

    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name.startsWith(this.COOKIE_PREFIX) && value) {
        try {
          const session = JSON.parse(decodeURIComponent(value)) as GameSessionCookie;
          
          // Accept all valid game IDs (both UUID and game-timestamp formats)
          
          // Check if session is still valid (within TTL)
          if (now - session.createdAt < ttlMs) {
            sessions.push(session);
          } else {
            // Remove expired cookie
            this.removeGameSession(session.gameId);
          }
        } catch (e) {
          console.error('Failed to parse game session cookie:', e);
        }
      }
    }

    // Sort by last played, most recent first
    return sessions.sort((a, b) => b.lastPlayed - a.lastPlayed);
  }

  // Get a specific game session
  static getGameSession(gameId: string): GameSessionCookie | null {
    const cookieName = `${this.COOKIE_PREFIX}${gameId}`;
    const cookies = document.cookie.split(';');
    
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === cookieName && value) {
        try {
          return JSON.parse(decodeURIComponent(value)) as GameSessionCookie;
        } catch (e) {
          console.error('Failed to parse game session cookie:', e);
          return null;
        }
      }
    }
    
    return null;
  }

  // Remove a game session cookie
  static removeGameSession(gameId: string): void {
    const cookieName = `${this.COOKIE_PREFIX}${gameId}`;
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }

  // Clear all game session cookies
  static clearAllGameSessions(): void {
    const sessions = this.getActiveGameSessions();
    sessions.forEach(session => this.removeGameSession(session.gameId));
  }

  // Format time for display
  static formatTimeAgo(timestamp: number): string {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    return 'Just now';
  }
}